import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { motion } from 'framer-motion';
import { LocalizedLink } from '@/components/LocalizedLink';
import { useT, useLang } from '@/lib/i18n';
import {
  MapPin, Phone, Mail, Award, Factory, Users, Calendar,
  Shield, Cog, ChevronRight, Heart, Lightbulb, Zap,
  Globe, Truck, Languages, FileCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  getOrganizationJsonLd,
  getBreadcrumbJsonLd,
  getLarsZimmermannAboutJsonLd,
  COMPANY,
  BASE_URL,
} from '@/lib/seo-constants';
import larsZimmermann from '@/assets/lars-zimmermann.webp';
import claraFernau from '@/assets/clara-fernau.webp';
import theodorFernau from '@/assets/theodor-fernau.png';
import tuvLogo from '@/assets/tuv-iso-9001.webp';

const FADE_UP = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

export default function UeberUns() {
  const t = useT();
  const lang = useLang();
  const timeline = [
    { year: '1995', text: t('Gründung der FERNAU Präzisionstechnik GmbH in Darmstadt durch Theodor Fernau.', 'Foundation of FERNAU Präzisionstechnik GmbH in Darmstadt by Theodor Fernau.') },
    { year: '2005', text: t('Erweiterung des Maschinenparks um 5-Achs-Simultanbearbeitung und Investition in moderne CAD/CAM-Systeme.', 'Expansion of the machine park with 5-axis simultaneous machining and investment in modern CAD/CAM systems.') },
    { year: '2020', text: t('Umstellung auf 100 % Ökostrom, Mitgliedschaft im Bundesverband Nachhaltige Wirtschaft und Aufbau des Consulting-Bereichs.', 'Switch to 100% renewable electricity, membership in the German Federal Association for Sustainable Economy and launch of the consulting division.') },
    { year: '2021', text: t('Generationswechsel: Clara Fernau übernimmt die Inhaberschaft und rückt in die Geschäftsführung auf. Theodor Fernau bleibt weiterhin Geschäftsführer.', 'Generation change: Clara Fernau takes over ownership and joins the management. Theodor Fernau remains a managing director.') },
    { year: '2025', text: t('Erstmalige Zertifizierung nach DIN EN ISO 9001:2015 und Ausbau der Kapazitäten für Serienfertigung.', 'First certification to DIN EN ISO 9001:2015 and expansion of capacity for series production.') },
    { year: '2025', text: t('Ernennung von Lars Zimmermann zum ersten Fremdgeschäftsführer der FERNAU Präzisionstechnik GmbH.', 'Appointment of Lars Zimmermann as the first external Managing Director of FERNAU Präzisionstechnik GmbH.') },
  ];
  const management = [
    { name: 'Clara Fernau', role: t('Inhaberin & Geschäftsführerin', 'Owner & Managing Director'), image: claraFernau },
    { name: 'Theodor Fernau', role: t('Geschäftsführer', 'Managing Director'), image: theodorFernau },
    { name: 'Lars Zimmermann', role: t('Geschäftsführer', 'Managing Director'), image: larsZimmermann },
  ];
  const values = [
    { icon: Users, title: t('Partnerschaftlich', 'Partnership-driven'), text: t('Offene und faire Zusammenarbeit auf Augenhöhe. Ihr Erfolg ist unser Antrieb.', 'Open and fair collaboration on eye level. Your success is our drive.') },
    { icon: Lightbulb, title: t('Innovativ', 'Innovative'), text: t('Kontinuierliche Investition in modernste CNC-Technologie und Prozessoptimierung.', 'Continuous investment in state-of-the-art CNC technology and process optimisation.') },
    { icon: Heart, title: t('Nachhaltig', 'Sustainable'), text: t('100 % Ökostrom, Lastenrad-Lieferung und aktives Engagement im BNW.', '100% renewable electricity, cargo-bike delivery and active membership in BNW.') },
    { icon: Zap, title: t('Flexibel', 'Flexible'), text: t('Einzelteile bis Kleinserien, kurzfristige Änderungen, termingerechte Realisierung.', 'Single parts to small series, short-notice changes, on-time delivery.') },
  ];
  const machines = [
    { category: t('CNC-Fräsen', 'CNC milling'), count: t('3 Maschinen', '3 machines'), details: t('HURCO VMX 30t, VMX 30i, VMX 42t – 3- bis 5-Achs-Bearbeitung', 'HURCO VMX 30t, VMX 30i, VMX 42t – 3- to 5-axis machining') },
    { category: t('CNC-Drehen', 'CNC turning'), count: t('4 Maschinen', '4 machines'), details: '3× EMCO TURN E65, 1× EMCO TURN E25' },
    { category: t('Konventionell', 'Conventional'), count: t('3 Maschinen', '3 machines'), details: 'GDW LZ 400 VS1, GDW Z 280 VS, Weiler Condor VC' },
    { category: t('Laser', 'Laser'), count: t('1 Gerät', '1 unit'), details: t('TRUMPF Lasersign PL2 – Laserbeschriftung & Gravur', 'TRUMPF Lasersign PL2 – laser marking & engraving') },
  ];
  const jsonLd = [
    getOrganizationJsonLd(lang),
    getBreadcrumbJsonLd([{ name: t('Über uns', 'About us'), path: '/ueber-uns' }], lang),
    getLarsZimmermannAboutJsonLd(lang),
  ];

  return (
    <Layout>
      <SEOHead
        title={t('Über uns – CNC-Zerspanung seit 1995', 'About us – CNC machining since 1995')}
        description={t('Lernen Sie FERNAU Präzisionstechnik kennen: Gegründet 1995 in Darmstadt, ISO 9001 zertifiziert, 11 Maschinen. Geschäftsführung, Werte und Geschichte.', 'Get to know FERNAU Präzisionstechnik: founded 1995 in Darmstadt, Germany, ISO 9001 certified, 11 machines. Management, values and history.')}
        path="/ueber-uns"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative py-20 sm:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" />
        <div className="container mx-auto px-4 sm:px-6 relative">
          <motion.span className="text-primary font-mono text-sm uppercase tracking-wider" {...FADE_UP}>
            {t('Seit 1995 in Darmstadt', 'In Darmstadt since 1995')}
          </motion.span>
          <motion.h1
            className="mt-4 text-4xl sm:text-5xl md:text-6xl font-bold leading-tight"
            {...FADE_UP}
            transition={{ delay: 0.1 }}
          >
            {t(<>Präzision ist <span className="text-primary">unsere DNA</span></>, <>Precision is <span className="text-primary">our DNA</span></>)}
          </motion.h1>
          <motion.p
            className="mt-6 max-w-2xl text-lg text-muted-foreground leading-relaxed"
            {...FADE_UP}
            transition={{ delay: 0.2 }}
          >
            {t(
              'Die FERNAU Präzisionstechnik GmbH ist ein inhabergeführter CNC-Zerspanungsbetrieb mit Sitz in Darmstadt. Seit der Gründung 1995 fertigen wir hochpräzise Dreh- und Frästeile für anspruchsvolle Branchen wie Medizintechnik, Luftfahrt, Automotive und Energietechnik.',
              'FERNAU Präzisionstechnik GmbH is an owner-managed CNC machining company based in Darmstadt, Germany. Since 1995 we have manufactured high-precision turned and milled parts for demanding industries such as medical technology, aerospace, automotive and energy.'
            )}
          </motion.p>

          {/* Quick Facts */}
          <motion.div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4" {...FADE_UP} transition={{ delay: 0.3 }}>
            {[
              { icon: Calendar, value: '1995', label: t('Gegründet', 'Founded') },
              { icon: Factory, value: '11', label: t('Maschinen', 'Machines') },
              { icon: Award, value: 'ISO 9001', label: t('Zertifiziert', 'Certified') },
              { icon: Shield, value: '100 %', label: 'Made in Germany' },
            ].map((fact) => (
              <div key={fact.label} className="card-industrial p-5 text-center">
                <fact.icon className="w-6 h-6 text-primary mx-auto mb-2" />
                <div className="text-2xl font-bold font-mono text-primary">{fact.value}</div>
                <div className="text-sm text-muted-foreground mt-1">{fact.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Geschichte / Timeline */}
      <section className="py-20 relative">
        <div className="absolute inset-0 steel-texture" />
        <div className="container mx-auto px-4 sm:px-6 relative">
          <motion.h2 className="text-3xl sm:text-4xl font-bold mb-12" {...FADE_UP}>
            {t('Unsere ', 'Our ')}<span className="text-primary">{t('Geschichte', 'history')}</span>
          </motion.h2>

          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-4 sm:left-1/2 top-0 bottom-0 w-px bg-border/50 -translate-x-1/2 hidden sm:block" />

            <div className="space-y-10">
              {timeline.map((item, i) => (
                <motion.div
                  key={`${item.year}-${i}`}
                  className={`flex flex-col sm:flex-row items-start gap-4 sm:gap-8 ${i % 2 === 0 ? 'sm:flex-row' : 'sm:flex-row-reverse'}`}
                  {...FADE_UP}
                  transition={{ delay: i * 0.1 }}
                >
                  <div className={`sm:w-1/2 ${i % 2 === 0 ? 'sm:text-right' : 'sm:text-left'}`}>
                    <div className="card-industrial p-6">
                      <span className="text-primary font-mono font-bold text-lg">{item.year}</span>
                      <p className="mt-2 text-muted-foreground">{item.text}</p>
                    </div>
                  </div>
                  <div className="hidden sm:flex w-4 h-4 rounded-full bg-primary shrink-0 mt-6 relative z-10" />
                  <div className="sm:w-1/2" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Geschäftsführung */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.h2 className="text-3xl sm:text-4xl font-bold mb-4" {...FADE_UP}>
            {t('Geschäfts', 'Manage')}<span className="text-primary">{t('führung', 'ment')}</span>
          </motion.h2>
          <motion.p className="text-muted-foreground max-w-2xl mb-12" {...FADE_UP} transition={{ delay: 0.1 }}>
            {t(
              'Seit 2023 wird FERNAU von einer neuen Generation geführt, die Tradition und Innovation vereint. Gemeinsam treiben wir den Wandel hin zu nachhaltiger, digitaler Fertigung voran.',
              'Since 2023 FERNAU is led by a new generation that combines tradition and innovation. Together we drive the transformation towards sustainable, digital manufacturing.',
            )}
          </motion.p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {management.map((person, i) => (
              <motion.div
                key={person.name}
                {...FADE_UP}
                transition={{ delay: i * 0.1 }}
              >
                <div className="card-industrial p-6 text-center">
                  <div className="w-20 h-20 rounded-full bg-secondary mx-auto mb-4 flex items-center justify-center overflow-hidden">
                    <img
                      src={person.image}
                      alt={`${person.name}, ${person.role}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <h3 className="text-lg font-semibold">{person.name}</h3>
                  <p className="text-primary text-sm font-mono mt-1">{person.role}</p>
                </div>
                {person.name === 'Lars Zimmermann' && (
                  <p className="mt-4 text-sm text-muted-foreground leading-relaxed text-center sm:text-left">
                    {t(
                      'Geschäftsführer Lars Zimmermann ist zertifizierter ISO/IEC 42001 Senior Lead Auditor und ISO/IEC 27001 Lead Auditor (PECB Certified Trainer & Partner). Unter der eigenständigen Marke ',
                      'Managing Director Lars Zimmermann is a certified ISO/IEC 42001 Senior Lead Auditor and ISO/IEC 27001 Lead Auditor (PECB Certified Trainer & Partner). Under the independent brand ',
                    )}
                    <a
                      href="https://der-ki-auditor.de"
                      target="_blank"
                      rel="noopener noreferrer external"
                      className="text-primary hover:underline"
                    >
                      Der KI-Auditor
                    </a>
                    {t(
                      ' begleitet er Unternehmen bei KI-Managementsystemen nach ISO 42001, Informationssicherheit nach ISO 27001 und der Umsetzung des EU AI Act.',
                      ' he accompanies companies with AI management systems according to ISO 42001, information security according to ISO 27001 and the implementation of the EU AI Act.',
                    )}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Werte */}
      <section className="py-20 relative">
        <div className="absolute inset-0 steel-texture" />
        <div className="container mx-auto px-4 sm:px-6 relative">
          <motion.h2 className="text-3xl sm:text-4xl font-bold mb-12" {...FADE_UP}>
            {t('Unsere ', 'Our ')}<span className="text-primary">{t('Werte', 'values')}</span>
          </motion.h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <motion.div key={v.title} className="card-industrial p-6" {...FADE_UP} transition={{ delay: i * 0.1 }}>
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <v.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">{v.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{v.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Qualität & Zertifizierung */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...FADE_UP}>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                {t('Qualität nach ', 'Quality per ')}<span className="text-primary">ISO 9001:2015</span>
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                {t(
                  'Unsere Zertifizierung nach DIN EN ISO 9001:2015 ist kein Marketinginstrument, sondern gelebte Praxis. Jedes Bauteil durchläuft definierte Prüfprozesse, von der Wareneingangskontrolle bis zur Endabnahme mit dokumentierter Rückverfolgbarkeit.',
                  'Our DIN EN ISO 9001:2015 certification is not a marketing tool but lived practice. Every part runs through defined inspection processes, from incoming goods to final acceptance with documented traceability.',
                )}
              </p>
              <p className="text-muted-foreground leading-relaxed mb-6">
                {t(
                  'Regelmäßige interne Audits und die kontinuierliche Verbesserung unserer Prozesse garantieren gleichbleibend hohe Qualität, unabhängig von Losgröße und Materialvielfalt.',
                  'Regular internal audits and continuous process improvement guarantee consistent high quality, regardless of batch size or material variety.',
                )}
              </p>
              <div className="flex flex-wrap gap-3">
                {[
                  t('Wareneingangskontrolle', 'Incoming inspection'),
                  t('Prozessbegleitende Prüfung', 'In-process inspection'),
                  t('Endabnahme', 'Final acceptance'),
                  t('Rückverfolgbarkeit', 'Traceability'),
                ].map((item) => (
                  <span key={item} className="px-3 py-1.5 bg-primary/10 text-primary text-sm rounded-full font-mono">
                    {item}
                  </span>
                ))}
              </div>
            </motion.div>

            <motion.div className="flex justify-center" {...FADE_UP} transition={{ delay: 0.2 }}>
              <div className="card-industrial p-8 max-w-xs text-center">
                <a
                  href="http://www.tuvsud.com/ms-zert"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="TÜV SÜD ISO 9001 Zertifizierung"
                  className="inline-block transition-transform hover:scale-105"
                >
                  <img
                    src={tuvLogo}
                    alt="TÜV SÜD ISO 9001 – Zertifiziertes Qualitätsmanagementsystem"
                    className="mx-auto mb-4 max-h-48 object-contain"
                    draggable={false}
                  />
                </a>
                <p className="text-sm text-muted-foreground">
                  {t('Zertifiziert durch den TÜV nach DIN EN ISO 9001:2015', 'Certified by TÜV to DIN EN ISO 9001:2015')}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Maschinenpark-Übersicht */}
      <section className="py-20 relative">
        <div className="absolute inset-0 steel-texture" />
        <div className="container mx-auto px-4 sm:px-6 relative">
          <motion.h2 className="text-3xl sm:text-4xl font-bold mb-4" {...FADE_UP}>
            {t('Unser ', 'Our ')}<span className="text-primary">{t('Maschinenpark', 'machine park')}</span>
          </motion.h2>
          <motion.p className="text-muted-foreground max-w-2xl mb-12" {...FADE_UP} transition={{ delay: 0.1 }}>
            {t(
              'Mit 11 Maschinen decken wir ein breites Fertigungsspektrum ab – von filigranen Präzisionsteilen bis hin zu komplexen 5-Achs-Bauteilen.',
              'With 11 machines we cover a broad manufacturing spectrum — from delicate precision parts to complex 5-axis components.',
            )}
          </motion.p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {machines.map((m, i) => (
              <motion.div key={m.category} className="card-industrial p-6" {...FADE_UP} transition={{ delay: i * 0.1 }}>
                <Cog className="w-8 h-8 text-primary mb-4" />
                <h3 className="font-semibold text-lg">{m.category}</h3>
                <p className="text-primary font-mono text-sm mt-1">{m.count}</p>
                <p className="text-muted-foreground text-sm mt-3">{m.details}</p>
              </motion.div>
            ))}
          </div>

          <motion.div className="mt-8" {...FADE_UP} transition={{ delay: 0.4 }}>
            <Button variant="outline" asChild>
                <LocalizedLink to="/maschinenpark" className="inline-flex items-center gap-2">
                  {t('Maschinenpark im Detail', 'Machine park in detail')}
                <ChevronRight className="w-4 h-4" />
                </LocalizedLink>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Standort */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.h2 className="text-3xl sm:text-4xl font-bold mb-12" {...FADE_UP}>
            {t('Standort ', 'Location ')}<span className="text-primary">Darmstadt</span>
          </motion.h2>

          <div className="grid lg:grid-cols-2 gap-12">
            <motion.div className="card-industrial p-8" {...FADE_UP}>
              <h3 className="font-semibold text-lg mb-6">{t('Kontaktdaten', 'Contact details')}</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">FERNAU Präzisionstechnik GmbH</p>
                    <address className="not-italic text-muted-foreground text-sm mt-1">
                      {COMPANY.address.street}<br />
                      {COMPANY.address.zip} {COMPANY.address.city}
                    </address>
                  </div>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-primary shrink-0" />
                  <a href={`tel:${COMPANY.phone}`} className="font-mono text-sm hover:text-primary transition-colors">
                    {COMPANY.phoneDisplay}
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-primary shrink-0" />
                  <a href={`mailto:${COMPANY.email}`} className="text-sm hover:text-primary transition-colors">
                    {COMPANY.email}
                  </a>
                </li>
              </ul>

              <div className="mt-8 pt-6 border-t border-border/30">
                <h4 className="font-medium text-sm mb-3">{t('Handelsregister', 'Commercial register')}</h4>
                <p className="text-muted-foreground text-sm">
                  {COMPANY.registrationCourt}, {COMPANY.registrationNumber}
                </p>
                <p className="text-muted-foreground text-sm mt-1">
                  {t('USt-IdNr.: ', 'VAT ID: ')}{COMPANY.vatId}
                </p>
              </div>
            </motion.div>

            <motion.div className="card-industrial p-8" {...FADE_UP} transition={{ delay: 0.1 }}>
              <h3 className="font-semibold text-lg mb-6">{t('Warum Darmstadt?', 'Why Darmstadt?')}</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                {t(
                  'Darmstadt ist ein Zentrum für Wissenschaft und Technologie im Herzen des Rhein-Main-Gebiets. Die Nähe zu Forschungseinrichtungen wie der TU Darmstadt, dem GSI Helmholtzzentrum und der ESA macht den Standort ideal für einen Präzisionsfertiger, der Branchen wie Messtechnik, Luft- und Raumfahrt und Medizintechnik bedient.',
                  'Darmstadt is a hub for science and technology in the heart of the Rhine-Main region. Proximity to research institutions such as TU Darmstadt, the GSI Helmholtz Centre and the ESA makes the site ideal for a precision manufacturer serving metrology, aerospace and medical technology.',
                )}
              </p>
              <p className="text-muted-foreground leading-relaxed">
                {t(
                  'Über die Autobahnen A5 und A67 sind wir aus dem gesamten Rhein-Main-Gebiet schnell erreichbar. Regionale Kunden beliefern wir umweltfreundlich per Lastenrad.',
                  'Via the A5 and A67 motorways we are easily reachable from across the Rhine-Main region. Regional customers are supplied sustainably by cargo bike.',
                )}
              </p>

              <div className="mt-8">
                <Button variant="hero" asChild>
                  <LocalizedLink to="/kontakt">{t('Kontakt aufnehmen', 'Get in touch')}</LocalizedLink>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Internationale Fertigung – neutrale Belege, keine Kundennamen */}
      <section className="py-20 bg-card border-t border-border">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-2xl mb-10">
            <h2 className="text-3xl font-bold">
              {t('Internationale Fertigung', 'International manufacturing')}
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              {t(
                'FERNAU fertigt seit 1995 auch für Kunden außerhalb Deutschlands. Aus Diskretionsgründen nennen wir keine Kundennamen. Unsere internationale Reichweite belegen wir über gelieferte Zolldokumente, EUR.1-Zertifikate und den regelmäßigen Versand in EU-Länder, die Schweiz, Großbritannien sowie per Luftfracht in die USA und den asiatischen Raum. Für unsere Kunden ist Diskretion Teil unserer Arbeit.',
                'FERNAU has been manufacturing for customers outside Germany since 1995. Out of respect for our customers we do not publish customer names. Our international reach is documented instead through delivered customs paperwork, EUR.1 certificates and regular shipping to EU countries, Switzerland, the United Kingdom and by air freight to the United States and Asia. Discretion is part of the service.',
              )}
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Globe, value: t('DE · EU · Weltweit', 'DE · EU · Worldwide'), label: t('Versandzonen', 'Shipping zones') },
              { icon: Truck, value: t('24–48h · 2–5 Tage · 5–10 Tage', '24–48h · 2–5 days · 5–10 days'), label: t('Laufzeiten DE / EU / Luftfracht', 'Lead times DE / EU / air freight') },
              { icon: Languages, value: t('Deutsch & Englisch', 'German & English'), label: t('Angebote & Doku', 'Quotes & documentation') },
              { icon: FileCheck, value: 'EUR.1 · EN 10204 3.1', label: t('Zoll- & Materialzeugnisse', 'Customs & material certificates') },
            ].map((item) => (
              <div key={item.label} className="card-industrial p-5">
                <item.icon className="w-6 h-6 text-primary" />
                <div className="mt-3 text-sm font-semibold">{item.value}</div>
                <div className="mt-1 text-xs text-muted-foreground">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
